import type { VehicleRow, SpecRow } from "@/lib/vehicle-queries";
import type { FaqItem } from "@/components/FaqBlock";
import { article, cleanTransmission, isElectric, isPhev, mpgUnit, sameFamily } from "@/lib/vehicle-text";

export interface ModelFaqInput {
  makeName: string;
  modelName: string;
  vehicles: VehicleRow[];
  towingSpecs?: SpecRow[];
}

/**
 * Conversational FAQ for the model hub page. Questions are phrased exactly the
 * way people ask assistants, and every answer is a single data-backed sentence.
 */
export function buildModelFaqs(input: ModelFaqInput): FaqItem[] {
  const { makeName, modelName, vehicles, towingSpecs = [] } = input;
  if (vehicles.length === 0) return [];

  const fullName = `${makeName} ${modelName}`;
  const years = vehicles.map((v) => v.year);
  const latestYear = Math.max(...years);
  const earliestYear = Math.min(...years);
  const named = `${latestYear} ${fullName}`;

  const latest = vehicles.find((v) => v.year === latestYear) ?? vehicles[0]!;
  const family = sameFamily(vehicles, latest);
  const best = family.reduce<VehicleRow | null>(
    (b, v) => (v.mpg_combined && (!b || (b.mpg_combined ?? 0) < v.mpg_combined) ? v : b),
    null,
  );
  const tow = towingSpecs.reduce<SpecRow | null>(
    (b, s) =>
      s.towing_capacity_lbs && (!b || (b.towing_capacity_lbs ?? 0) < s.towing_capacity_lbs) ? s : b,
    null,
  );
  const ev = family.reduce<VehicleRow | null>(
    (b, v) => (v.ev_range_miles && (!b || (b.ev_range_miles ?? 0) < v.ev_range_miles) ? v : b),
    null,
  );

  const faqs: FaqItem[] = [];

  faqs.push({
    q: `What is the fuel economy of the ${named}?`,
    a: best?.mpg_combined
      ? `The ${fullName} is EPA-rated at up to ${best.mpg_combined} ${mpgUnit(best)} combined${
          best.mpg_city && best.mpg_hwy ? ` (${best.mpg_city} city / ${best.mpg_hwy} highway)` : ""
        }${best.year !== latestYear ? `, a historical peak recorded on the ${best.year} model year` : ""}.`
      : `EPA combined fuel economy for the ${fullName} varies by trim and drivetrain — see the year-by-year table above.`,
  });

  if (tow?.towing_capacity_lbs) {
    faqs.push({
      q: `What is the towing capacity of the ${named}?`,
      a: `When properly equipped, the ${fullName} tows up to ${tow.towing_capacity_lbs.toLocaleString()} lbs${
        tow.towing_config ? ` (${tow.towing_config})` : ""
      }${tow.year !== latestYear ? `, a historical peak recorded on the ${tow.year} model year` : ""}.`,
    });
  }

  faqs.push({
    q: `What engine does the ${named} have?`,
    a: isElectric(latest)
      ? `The ${fullName} is fully electric — it uses an electric motor with a single-speed transmission instead of a combustion engine.`
      : latest.displacement || latest.cylinders
      ? `The ${fullName} uses a ${[
          latest.displacement ? `${latest.displacement}L` : null,
          latest.cylinders ? `${latest.cylinders}-cylinder` : null,
        ]
          .filter(Boolean)
          .join(" ")} engine${
          cleanTransmission(latest.transmission)
            ? ` paired with ${article(cleanTransmission(latest.transmission))} ${cleanTransmission(
                latest.transmission,
              )} transmission`
            : ""
        }.`
      : `Engine options for the ${fullName} vary by trim — the full lineup is listed in the table above.`,
  });

  if (ev?.ev_range_miles) {
    const phev = isPhev(ev);
    faqs.push({
      q: phev
        ? `How far can the ${named} go on a full charge and tank?`
        : `How far can the ${named} go on a full charge?`,
      a: phev
        ? `As a plug-in hybrid, the ${fullName} has an EPA-rated total driving range of up to ${ev.ev_range_miles} miles combining electric and gasoline operation.`
        : `The ${fullName} has an EPA-rated electric driving range of up to ${ev.ev_range_miles} miles.`,
    });
  }

  faqs.push({
    q: `What years of the ${fullName} are covered here?`,
    a: `This page documents ${vehicles.length} verified ${fullName} configurations across model years ${earliestYear}–${latestYear}.`,
  });

  // Which model year actually performs best — a genuinely per-model answer,
  // computed from this vehicle's own year-by-year EPA record.
  const byYear = new Map<number, number>();
  for (const v of vehicles) {
    if (typeof v.mpg_combined === "number") {
      byYear.set(v.year, Math.max(byYear.get(v.year) ?? 0, v.mpg_combined));
    }
  }
  if (byYear.size > 1) {
    const ranked = [...byYear.entries()].sort((a, b) => b[1] - a[1]);
    const [bestYear, bestVal] = ranked[0]!;
    const [worstYear, worstVal] = ranked[ranked.length - 1]!;
    faqs.push({
      q: `Which model year of the ${fullName} is the most fuel efficient?`,
      a:
        bestVal === worstVal
          ? `Every documented ${fullName} model year lands on the same ${bestVal} MPG combined peak, so fuel economy is not a reason to prefer one year over another.`
          : `The ${bestYear} ${fullName} holds the highest combined rating at ${bestVal} MPG; the ${worstYear} sits lowest at ${worstVal} MPG, a difference of ${
              bestVal - worstVal
            } MPG (about ${Math.round(((bestVal - worstVal) / worstVal) * 100)}%) across the ${byYear.size} rated years on file.`,
    });
  }

  // Trim/engine breadth differs sharply between models and is not derivable
  // from the model name alone.
  const displacements = Array.from(
    new Set(
      vehicles
        .map((v) => (v.displacement == null ? null : Number(v.displacement)))
        .filter((d): d is number => typeof d === "number" && Number.isFinite(d)),
    ),
  ).sort((a, b) => a - b);
  if (displacements.length > 0) {
    faqs.push({
      q: `How many engine options does the ${fullName} have?`,
      a:
        displacements.length === 1
          ? `Across every year on record the ${fullName} uses one displacement, ${displacements[0]}L, so trims differ by drivetrain and equipment rather than engine size.`
          : `The records list ${displacements.length} distinct displacements for the ${fullName} — ${displacements
              .map((d) => `${d}L`)
              .join(", ")} — which is why combined MPG varies between trims of the same year.`,
    });
  }

  const driveMix = Array.from(new Set(vehicles.map((v) => v.drive).filter(Boolean))) as string[];
  faqs.push({
    q: `Is the ${named} front-wheel drive or all-wheel drive?`,
    a:
      driveMix.length > 1
        ? `The ${fullName} appears in ${driveMix.length} drivetrain layouts across the years documented here — ${driveMix.join(
            ", ",
          )} — with the latest ${latestYear} record on ${latest.drive ?? "an unlisted layout"}.`
        : latest.drive
          ? `Every ${fullName} configuration on record here is ${latest.drive}; no alternative drivetrain appears in the ${earliestYear}–${latestYear} data.`
          : `Drivetrain availability for the ${fullName} changes by trim and model year — check the table above for each variant.`,
  });

  return faqs;
}
